import { Dispatch, Fragment, SetStateAction, useMemo, useState } from 'react';

import { Button } from '@components/button';
import { FormInstance } from '@components/form/create-form';
import { IconAddPlus } from '@components/icons/add-plus';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingGroup } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { uuid } from '@utils/uuid';

import { ConditionComponent, getComparatorIds, Condition } from './condition-component';

export type ConditionType = 'all' | 'any';

export const useConditionState = (initialConditions: Condition[] = []) => {
  const [conditions, setConditions] = useState<Condition[]>(
    initialConditions.map((condition) => ({
      ...condition,
      id: condition.id ?? uuid(),
    }))
  );

  const conditionsChanged = useMemo(() => {
    return (
      conditions.length !== initialConditions.length ||
      conditions.some((condition, index) => {
        const initialCondition = initialConditions[index];
        return (
          !initialCondition ||
          condition.field?.value !== initialCondition.field?.value ||
          condition.comparator !== initialCondition.comparator ||
          condition.value !== initialCondition.value
        );
      })
    );
  }, [conditions, initialConditions]);

  const handleAddCondition = () => {
    const newConditions = [
      ...conditions,
      {
        id: uuid(),
        field: { value: '' },
        comparator: undefined,
        value: undefined,
      },
    ];
    setConditions(newConditions);
  };

  const handleRemoveCondition = (conditionId: string) => {
    setConditions(conditions.filter((condition) => condition.id !== conditionId));
  };

  return {
    conditions,
    setConditions,
    conditionsChanged,
    handleAddCondition,
    handleRemoveCondition,
  };
};

export const processConditionsForSubmission = (
  conditions: Condition[],
  formValues: Record<string, any>
): Condition[] => {
  return conditions.map((condition) => {
    const { elementId, comparatorId, valueId } = getComparatorIds(condition.id);
    const value = formValues[valueId];
    return {
      id: condition.id,
      field: formValues[elementId],
      comparator: formValues[comparatorId],
      value,
      visible: !value,
    } as Condition;
  });
};

type ConditionsProps = {
  conditions: Condition[];
  conditionType?: ConditionType;
  variableGroups: FieldMappingGroup[];
  fieldMappingId: string;
  onAddCondition: () => void;
  onRemoveCondition: (conditionId: string) => void;
  setConditions: Dispatch<SetStateAction<Condition[]>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Form: FormInstance<any>;
  helpText?: string;
};

export const Conditions = ({
  conditions,
  conditionType: initialConditionType = 'all',
  variableGroups,
  fieldMappingId,
  onAddCondition,
  onRemoveCondition,
  setConditions,
  Form,
  helpText,
}: ConditionsProps) => {
  const { t } = useScopedTranslate(locale);
  const [conditionType, setConditionType] = useState(initialConditionType);

  const displayHelpText = helpText ?? t('conditionHelptext', { conditionType });

  return (
    <Stack space={6}>
      <Stack orientation="horizontal" justify="space-between" align="center">
        <Text font="regular14" text={displayHelpText} />
        <div style={{ width: '96px' }}>
          <Form.Select
            label={t('conditionType')}
            hideLabel
            id="conditionType"
            options={[
              { value: 'all', label: t('all') },
              { value: 'any', label: t('any') },
            ]}
            isClearable={false}
            defaultValue={initialConditionType}
            onChange={(value) => setConditionType(value as ConditionType)}
          />
        </div>
      </Stack>
      <Stack space={4}>
        {conditions.map((condition, index) => (
          <Fragment key={condition.id}>
            {index > 0 && (
              <Stack space={2} orientation="horizontal" justify="center" align="center">
                <Separator />
                <Text
                  font="semibold14"
                  style={{ textTransform: 'uppercase' }}
                  color="textSecondary"
                  text={conditionType === 'all' ? t('core.and') : t('core.or')}
                />
                <Separator />
              </Stack>
            )}
            <ConditionComponent
              key={condition.id}
              variableGroups={variableGroups}
              onRemove={() => onRemoveCondition(condition.id)}
              setConditions={setConditions}
              condition={condition}
              form={Form}
              index={index}
              fieldMappingId={fieldMappingId}
            />
          </Fragment>
        ))}
        <Stack orientation="horizontal">
          <Button
            onClick={onAddCondition}
            text={t('addElementLabel')}
            iconLeft={<IconAddPlus />}
            variant="secondary"
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    conditionHelptext: 'A traveller will be added if {conditionType} of the following matches',
    conditionType: 'Condition type',
    all: 'All',
    any: 'Any',
    addElementLabel: 'Add element',
  },
};
